import { Extension } from "@tiptap/core";

export interface TableResizeOptions {
  cellMinWidth: number;
  cellMaxWidth: number;
  rowMinHeight: number;
  rowMaxHeight: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableResize: {
      /**
       * Set current cell width in pixels
       */
      setCellWidth: (width: number) => ReturnType;
      /**
       * Set current cell height in pixels
       */
      setCellHeight: (height: number) => ReturnType;
      /**
       * Set table width in pixels
       */
      setTableWidthPixels: (width: number) => ReturnType;
    };
  }
}

export const TableResize = Extension.create<TableResizeOptions>({
  name: "tableResize",

  addOptions() {
    return {
      cellMinWidth: 25,
      cellMaxWidth: 2000,
      rowMinHeight: 25,
      rowMaxHeight: 1000,
    };
  },

  addCommands() {
    return {
      setCellWidth:
        (width: number) =>
        ({ state, dispatch }) => {
          if (!dispatch) return false;

          const { selection } = state;
          const { $from } = selection;

          let cellPos = null;
          for (let i = $from.depth; i > 0; i--) {
            const node = $from.node(i);
            if (
              node.type.name === "tableCell" ||
              node.type.name === "tableHeader"
            ) {
              cellPos = $from.before(i);
              break;
            }
          }

          if (cellPos === null) return false;

          const cell = state.doc.nodeAt(cellPos);
          if (!cell) return false;

          const currentStyle = cell.attrs.style || "";
          const newStyle =
            currentStyle
              .replace(/width:\s*\d+px;?\s*/g, "")
              .replace(/min-width:\s*\d+px;?\s*/g, "") +
            ` width: ${width}px; min-width: ${width}px;`;

          const tr = state.tr.setNodeMarkup(cellPos, null, {
            ...cell.attrs,
            style: newStyle.trim(),
          });

          dispatch(tr);
          return true;
        },

      setTableWidthPixels:
        (width: number) =>
        ({ state, dispatch, commands }) => {
          if (!dispatch) return false;

          const { selection } = state;
          const { $from } = selection;

          let tablePos = null;
          for (let i = $from.depth; i > 0; i--) {
            const node = $from.node(i);
            if (node.type.name === "table") {
              tablePos = $from.before(i);
              break;
            }
          }

          if (tablePos === null) return false;
          return commands.setTableWidth(`${width}px`);
        },

      setCellHeight:
        (height: number) =>
        ({ state, dispatch }) => {
          if (!dispatch) return false;

          const { selection } = state;
          const { $from } = selection;

          let cellPos = null;
          for (let i = $from.depth; i > 0; i--) {
            const node = $from.node(i);
            if (
              node.type.name === "tableCell" ||
              node.type.name === "tableHeader"
            ) {
              cellPos = $from.before(i);
              break;
            }
          }

          if (cellPos === null) return false;

          const cell = state.doc.nodeAt(cellPos);
          if (!cell) return false;

          const currentStyle = cell.attrs.style || "";
          const newStyle =
            currentStyle
              .replace(/height:\s*\d+px;?\s*/g, "")
              .replace(/min-height:\s*\d+px;?\s*/g, "") +
            ` height: ${height}px; min-height: ${height}px;`;

          const tr = state.tr.setNodeMarkup(cellPos, null, {
            ...cell.attrs,
            style: newStyle.trim(),
          });

          dispatch(tr);
          return true;
        },
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: ["table"],
        attributes: {
          style: {
            default: null,
            parseHTML: (element) => element.getAttribute("style"),
            renderHTML: (attributes) => {
              if (!attributes.style) {
                return {};
              }
              return { style: attributes.style };
            },
          },
        },
      },
      {
        types: ["tableCell", "tableHeader"],
        attributes: {
          style: {
            default: null,
            parseHTML: (element) => element.getAttribute("style"),
            renderHTML: (attributes) => {
              if (!attributes.style) {
                return {};
              }
              return { style: attributes.style };
            },
          },
        },
      },
    ];
  },
});
